/**
 * Coordinate validation utilities.
 *
 * Central guards for latitude/longitude values before they reach
 * the Google Maps API (panTo, setCenter, setPosition, LatLng, etc.).
 *
 * Every entry-point that handles geographic coordinates should use
 * these helpers so invalid values (undefined, null, NaN, Infinity,
 * empty strings, non-numeric strings) never cause an InvalidValueError.
 */

/**
 * Validates that a value is a finite number suitable for use as a coordinate.
 * Guards against undefined, null, NaN, Infinity, strings, and non-numeric values.
 */
export function isValidCoordinate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Validates a lat/lng pair. Both must be finite numbers.
 * Returns a type-guard on `lat` so TypeScript narrows it after the check.
 */
export function isValidLatLng(lat: unknown, lng: unknown): lat is number {
  return isValidCoordinate(lat) && isValidCoordinate(lng);
}

/**
 * Safely parses a coordinate from a string or number, returning `undefined`
 * when the value is not a valid finite number.
 *
 * Edge-case handling:
 *  - `null` / `undefined`       → undefined
 *  - `""` / `" "`               → undefined  (trimmed first — avoids Number(" ") = 0)
 *  - `"NaN"` / `"null"`         → undefined  (Number("NaN") = NaN → not finite)
 *  - `0` / `"0"`                → 0          (equator/prime-meridian preserved)
 *  - `"-0.18"` / `"139.69"`     → number     (valid coordinates)
 *  - `NaN` / `Infinity`         → undefined
 */
export function parseCoordinate(value: unknown): number | undefined {
  if (value == null) return undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : undefined;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  return undefined;
}
